import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toApiDateOnly } from '@/shared/utils/dateUtils';
import { priorityFromInt, priorityToInt } from '@/shared/lib/priority';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import type { Tag } from '@/features/tags/public';
import { convertToTaskSchema } from '../schemas';
import type { ConvertToTaskFormValues } from '../schemas';
import { useConvertNoteToTask } from './useConvertNoteToTask';
import { useInterpretNoteAsTask } from './useInterpretNoteAsTask';
import { useConvertToTaskOptions } from './useConvertToTaskOptions';
import { useConvertToTaskFieldSync } from './useConvertToTaskFieldSync';
import type { NoteTaskProposal } from '../types';

/**
 * `yyyy-MM-dd` to a Date at local midnight. Built from the parts rather than parsed:
 * `new Date('2026-08-17')` is UTC midnight, which renders as the 16th for anyone west
 * of UTC once the picker formats it in the viewer's zone.
 */
function toLocalDate(value: string | null): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/**
 * The whole convert-to-task form: react-hook-form setup, the note interpretation
 * that pre-fills it, every option list (via `useConvertToTaskOptions`) and the
 * field-dependency effects (via `useConvertToTaskFieldSync`).
 */
export function useConvertToTaskForm(noteId: string, noteTitle: string | null, open: boolean) {
  const { t } = useTranslation('notes');
  const { toast } = useToast();
  const convertToTask = useConvertNoteToTask(noteId);
  const interpretNote = useInterpretNoteAsTask(noteId);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const hasInterpretedRef = useRef(false);
  const interpretingNoteIdRef = useRef<string | null>(null);
  const [proposal, setProposal] = useState<NoteTaskProposal | null>(null);

  const activeOrgId = useOrganizationStore((s) => s.activeOrganizationId);

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<ConvertToTaskFormValues>({
      resolver: zodResolver(convertToTaskSchema),
      defaultValues: {
        organizationId: activeOrgId ?? '',
        title: noteTitle ?? '',
        description: '',
        projectId: '',
        boardId: '',
        statusId: '',
        groupId: null,
        priority: undefined,
        assigneeId: null,
        dueDate: null,
        startDate: null,
        estimatedMinutes: null,
        tags: [],
      },
    });

  const projectId = watch('projectId');
  const boardId = watch('boardId');

  // TagPicker works with Tag objects while the payload takes ids — the form field
  // stays the source of truth for what gets sent, this only backs the chips.
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const options = useConvertToTaskOptions(projectId, boardId, proposal);
  const { projectsLoading, contextProjects } = options;

  // Reset form and interpret note when the dialog opens for a new note.
  // Waits for the project list: interpreting first and sending an empty context
  // would throw away the one thing that lets the model resolve the project the
  // note names. A failed list still proceeds — it just interprets without that
  // context.
  useEffect(() => {
    // Always clear stale proposal state when the note changes so a result that
    // arrives after the dialog was reused never overwrites the wrong note.
    if (noteId !== interpretingNoteIdRef.current) {
      interpretingNoteIdRef.current = noteId;
      hasInterpretedRef.current = false;
      setProposal(null);
      reset({
        organizationId: activeOrgId ?? '',
        title: noteTitle ?? '',
        description: '',
        projectId: '',
        boardId: '',
        statusId: '',
        groupId: null,
        priority: undefined,
        assigneeId: null,
        dueDate: null,
        startDate: null,
        estimatedMinutes: null,
        tags: [],
      });
    }

    if (open && !projectsLoading && !hasInterpretedRef.current) {
      hasInterpretedRef.current = true;
      setIsInterpreting(true);
      const callerTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
      interpretNote.mutate(
        {
          callerTimeZoneId,
          // The model gets the names the dialog offers, so a note that abbreviates or
          // misspells one comes back as the project the user can actually pick.
          context: { projectNames: contextProjects.map((option) => option.label) },
        },
        {
          onSuccess: (result) => {
            // Discard the result if the user closed the dialog or opened it for
            // a different note while the interpretation was in flight.
            if (!open || interpretingNoteIdRef.current !== noteId) return;

            setProposal(result);

            if (result.title) setValue('title', result.title);
            if (result.description) setValue('description', result.description);

            // Pre-fill fields that don't depend on project selection.
            // The proposal's priority goes through the shared enum mapping, so a
            // code outside 1-4 leaves the field empty instead of picking a
            // neighbouring priority the note never asked for.
            const proposedPriority = priorityFromInt(result.priority);
            if (proposedPriority) {
              setValue('priority', priorityToInt(proposedPriority));
            }
            if (result.estimatedMinutes !== null && result.estimatedMinutes !== undefined) {
              setValue('estimatedMinutes', result.estimatedMinutes);
            }
            // Already resolved by the API against the caller's time zone — the
            // browser only turns `yyyy-MM-dd` into the local calendar day.
            const due = toLocalDate(result.dueDate);
            if (due) setValue('dueDate', due);
            const start = toLocalDate(result.startDate);
            if (start) setValue('startDate', start);
          },
          onError: () => {
            toast.error({ title: t('convertToTask.messages.interpretError') });
          },
          onSettled: () => {
            // Only clear the interpreting flag if this request still belongs to
            // the current dialog invocation.
            if (interpretingNoteIdRef.current === noteId) {
              setIsInterpreting(false);
            }
          },
        },
      );
    }
    if (!open) {
      hasInterpretedRef.current = false;
    }
  }, [
    open,
    projectsLoading,
    contextProjects,
    reset,
    interpretNote,
    setValue,
    noteId,
    noteTitle,
    activeOrgId,
    t,
    toast,
  ]);

  useConvertToTaskFieldSync({
    setValue,
    getValues,
    projectId,
    boardId,
    setSelectedTags,
    onlyBoard: options.onlyBoard,
    onlyStatus: options.onlyStatus,
    onlyProject: options.onlyProject,
    proposedProject: options.proposedProject,
    proposedStatus: options.proposedStatus,
    proposedGroup: options.proposedGroup,
    proposedAssignee: options.proposedAssignee,
  });

  const onSubmit = async (data: ConvertToTaskFormValues) => {
    try {
      await convertToTask.mutateAsync({
        projectId: data.projectId,
        boardId: data.boardId,
        statusId: data.statusId || undefined,
        groupId: data.groupId,
        title: data.title,
        description: data.description,
        priority: data.priority,
        assigneeId: data.assigneeId,
        // Date only, not a UTC instant: the field is a calendar day, and the API takes
        // the date part of whatever instant it receives. Sending local midnight as an
        // instant moved the day back for anyone at a positive UTC offset — Madrid
        // picking the 17th stored the 16th.
        dueDate: toApiDateOnly(data.dueDate),
        startDate: toApiDateOnly(data.startDate),
        estimatedMinutes: data.estimatedMinutes,
        tags: data.tags,
      });
      return true;
    } catch (error) {
      console.error('Error creating task from note:', error);
      return false;
    }
  };

  return {
    control,
    handleSubmit,
    onSubmit,
    isPending: convertToTask.isPending,
    isInterpreting,
    proposal,
    projectId,
    boardId,
    selectedTags,
    setSelectedTags,
    ...options,
  };
}
